import {
  Component,
  Inject,
  Injector,
  Input,
  isDevMode,
  OnChanges,
  OnDestroy,
  Type,
  ViewChild,
} from '@angular/core';
import { CdkDrag, CdkDragEnd } from '@angular/cdk/drag-drop';
import { Subscription } from 'rxjs';
import { DataService } from 'src/app/services/data.service';
import { PlaybackController } from '../playback-controller';
import { ToolbarDragService } from '../toolbar-drag.service';
import { ToolbarDrawerService } from '../toolbar-drawer.service';
import {
  SensorToolbarConfig,
  ToolbarControl,
  ToolbarControlContext,
  ToolbarControlDescriptor,
  ToolbarSlot,
  TOOLBAR_CONTROLS,
  TOOLBAR_CONTROL_CONTEXT,
} from '../toolbar-control.tokens';

interface ResolvedControl {
  component: Type<ToolbarControl>;
  injector: Injector;
  order: number;
}

/**
 * Thin toolbar HOST. Renders an ordered, per-sensor-filtered list of control
 * plugins for a given slot. Deciding which controls appear is pure configuration
 * (`SensorToolbarConfig`); adding a control means registering a descriptor
 * (`TOOLBAR_CONTROLS`) — no edits here.
 *
 * Angular 14 note: `NgComponentOutlet` cannot bind inputs, so each control receives
 * its `ToolbarControlContext` through a dedicated child `Injector`.
 */
@Component({
  selector: 'app-toolbar-host',
  templateUrl: './toolbar-host.component.html',
  styleUrls: ['./toolbar-host.component.scss'],
})
export class ToolbarHostComponent implements OnChanges, OnDestroy {
  @Input() slot: ToolbarSlot = 'playback';
  @Input() controller!: PlaybackController;
  @Input() config!: SensorToolbarConfig | null;

  /**
   * CSS selector for the element the bar may be dragged within, resolved with
   * `closest()` from the bar itself.
   *
   * The Leaflet container rather than `#map`: this host is deliberately generic
   * (see `dark`, below) and a hard-coded id would tie it to the one page it
   * happens to be mounted on today. An unmatched selector simply leaves the drag
   * unconstrained, so a bar rendered outside a map still drags.
   */
  @Input() dragBoundary = '.leaflet-container';

  resolved: ResolvedControl[] = [];

  /** True between drag start and drag end — suppresses the grip's tooltip. */
  dragging = false;

  /** The bar's own drag directive — the only thing that can put it back. */
  @ViewChild(CdkDrag) private dragRef?: CdkDrag;

  /**
   * Dark ground, as a CLASS ON THE BAR rather than only as a `.dark-theme`
   * ancestor selector.
   *
   * Its sidebar siblings can rely on the body class alone because they live
   * inside the panel, which is painted by the same global sheet. This bar does
   * not: it renders inside the Leaflet map, whose subtree the theme sheet never
   * touches, and it is the piece most likely to be reused somewhere the body
   * class is absent (a preview, a report capture, an embed). Reading the same
   * `DataService` stream the map component itself subscribes to makes the bar's
   * ground depend on the app's theme state instead of on where it happens to be
   * mounted. The `.dark-theme` selector is kept alongside it, so whichever
   * signal is present wins and the two can never disagree into a light bar on a
   * dark page.
   */
  dark = false;

  private _themeSub?: Subscription;

  constructor(
    @Inject(TOOLBAR_CONTROLS) private registry: ToolbarControlDescriptor[],
    private parentInjector: Injector,
    private data: DataService,
    /** Public: the template binds `drag.position` straight to cdkDrag. */
    public drag: ToolbarDragService,
    private drawer: ToolbarDrawerService,
  ) {
    // BehaviorSubject-backed, so this emits the current theme synchronously —
    // the bar is never painted light for a frame before correcting itself.
    this._themeSub = this.data.darkTheme.subscribe((v) => (this.dark = !!v));
  }

  ngOnChanges(): void {
    this._resolve();
  }

  ngOnDestroy(): void {
    this._themeSub?.unsubscribe();
  }

  /**
   * A drawer is anchored to the button that opened it and does not follow a
   * moving bar, so it is dismissed rather than left floating over the map beside
   * the control it belongs to. The grip's tooltip is anchored the same way but
   * DOES follow the bar, so it is suppressed for the length of the gesture.
   */
  onDragStarted(): void {
    this.dragging = true;
    this.drawer.close();
  }

  /** Remember where the bar was left, so the next bar to mount starts there. */
  onDragEnded(event: CdkDragEnd): void {
    this.dragging = false;
    this.drag.set(event.source.getFreeDragPosition());
  }

  /**
   * Double-click the grip to send the bar home.
   *
   * The one way back from a position that has stopped making sense — a window
   * resized narrower after the bar was parked at the right edge leaves it
   * clamped somewhere unhelpful, and dragging it back is not always possible.
   *
   * Through the directive, not the stored offset: cdkDrag applies
   * `cdkDragFreeDragPosition` when that binding's REFERENCE changes, and the
   * store is mutated in place (see `ToolbarDragService`). `reset()` is what
   * actually clears the transform; the store is cleared alongside it so the next
   * bar to mount also starts at the bottom centre.
   */
  resetPosition(): void {
    this.dragRef?.reset();
    this.drag.reset();
  }

  private _resolve(): void {
    if (!this.controller || !this.config) {
      this.resolved = [];
      return;
    }
    // A lookup, not a ternary chain: the old chain sent any slot it did not
    // name to `playback`, so a newly added slot rendered the playback controls
    // without erroring — silently wrong. With the slot as the key, a new slot
    // is either configured or empty, never someone else's bar.
    const slotConfig = this.config[this.slot] ?? [];

    this.resolved = slotConfig
      .filter((c) => c.enabled !== false)
      .map((c): ResolvedControl | null => {
        const desc = this.registry.find(
          (d) => d.id === c.id && d.slots.includes(this.slot),
        );
        if (!desc) {
          this._warnUnresolved(c.id);
          return null;
        }
        const context: ToolbarControlContext = {
          controller: this.controller,
          slot: this.slot,
          options: c.options ?? {},
        };
        const injector = Injector.create({
          providers: [{ provide: TOOLBAR_CONTROL_CONTEXT, useValue: context }],
          parent: this.parentInjector,
        });
        return {
          component: desc.component,
          injector,
          order: c.order ?? desc.defaultOrder,
        };
      })
      .filter((r): r is ResolvedControl => r !== null)
      .sort((a, b) => a.order - b.order);
  }

  /**
   * Already-reported `slot/id` pairs. Static rather than per-instance: four bars
   * mount for one sensor and each re-resolves on every `@Input` change, so
   * without this a single bad entry reports on every pass, from every slot.
   */
  private static readonly _reported = new Set<string>();

  /**
   * A config entry that matches no descriptor renders nothing, and did so in
   * SILENCE. Both ways of reaching that state have already shipped: the
   * port-call button sat on the playback bar for a release doing nothing, and
   * the popup's radar button was dead from the day it was written. Neither is
   * visible in a diff — the registration reads as complete and the control
   * simply never appears — and neither throws, so nothing surfaces it at all.
   *
   * The two causes need different fixes, so they are reported apart: an id that
   * is not registered is a missing descriptor in `TOOLBAR_CONTROLS`, while an id
   * that is registered but not for THIS slot is a `slots` whitelist that
   * disagrees with `SENSOR_TOOLBAR_CONFIGS`. The second is the one that reads as
   * correct in both files while being wrong across them.
   *
   * Dev mode only, and a warning rather than a throw: a control that fails to
   * resolve costs a button, which is not a reason to take the map down in front
   * of a user.
   */
  private _warnUnresolved(id: string): void {
    if (!isDevMode()) return;
    const key = `${this.config?.operatorKey ?? '?'}/${this.slot}/${id}`;
    if (ToolbarHostComponent._reported.has(key)) return;
    ToolbarHostComponent._reported.add(key);

    const registered = this.registry.find((d) => d.id === id);
    const where = `${this.config?.operatorKey ?? 'unknown operator'} · '${this.slot}' slot`;
    console.warn(
      registered
        ? `[toolbar] '${id}' is configured for ${where} but its descriptor whitelists ` +
            `only [${registered.slots.join(', ')}]. Add '${this.slot}' to its \`slots\` in ` +
            `TOOLBAR_CONTROLS, or drop the entry from SENSOR_TOOLBAR_CONFIGS. ` +
            `The control renders nothing until the two agree.`
        : `[toolbar] '${id}' is configured for ${where} but no descriptor is registered ` +
            `under that id. Add one to TOOLBAR_CONTROLS (and declare the component in the ` +
            `module), or drop the entry from SENSOR_TOOLBAR_CONFIGS.`,
    );
  }
}
